import type { RecordsResponse, SeasonCard, SpellGroup, SpellsResponse, Stat } from '../api.ts';
import {
  agoLabel,
  chipRow,
  el,
  escapeHtml,
  formatDate,
  formatDateLong,
  formatTime,
  nl,
  temp,
} from '../components/ui.ts';

/** Records als paar: wat het dit jaar werd, en wat het ooit was. */
export function recordTiles(records: RecordsResponse): HTMLElement {
  const grid = el('div', 'tiles rise');

  for (const rec of records.paired) {
    const shown = rec.thisYear ?? rec.ever;
    if (!shown) continue;

    const value = `${rec.unit === '°C' ? temp(shown.value) : nl(shown.value, rec.unit === 'mm' ? 1 : 0)} ${rec.unit}`;
    const when = shown.at
      ? `${formatDateLong(shown.date)}, ${formatTime(shown.at)}`
      : formatDateLong(shown.date);

    const ever = rec.ever
      ? `${rec.unit === '°C' ? temp(rec.ever.value) : nl(rec.ever.value, rec.unit === 'mm' ? 1 : 0)} ${rec.unit} · ${formatDateLong(rec.ever.date)}`
      : '';

    grid.append(
      el(
        'div',
        'tile',
        `<div class="k">${escapeHtml(rec.label)} ${rec.thisYear ? `in ${records.year}` : 'ooit'}</div>
         <div class="v ${rec.tone === 'neutral' ? '' : rec.tone}">${escapeHtml(value)}</div>
         <div class="when">${escapeHtml(when)}</div>
         ${
           rec.thisYear && ever
             ? `<div class="ever"><span class="k">ooit</span><span>${escapeHtml(ever)}</span></div>`
             : ''
         }`,
      ),
    );
  }

  return grid;
}

/**
 * De extremen-sectie: één grote kaart met de periode die er nu toe doet, en
 * daarnaast de vier soorten achter tabbladen.
 *
 * De kaart volgt het gekozen tabblad. Welk tabblad bij het openen bovenaan
 * staat kiest de server (`suggested`); is er al maanden niets uitzonderlijks
 * gebeurd, dan toont de kaart het lopende seizoen in plaats van een gebeurtenis
 * van een half jaar geleden.
 */
export function extremesSection(
  data: SpellsResponse,
  state: { kind: string; season: boolean; onKind: (kind: string) => void; onSeason: () => void },
): HTMLElement {
  const grid = el('div', 'extremes rise');
  const active = data.groups.find((g) => g.kind === state.kind) ?? data.groups[0];

  const hero =
    state.season && data.season
      ? seasonHero(data.season)
      : active
        ? spellHero(active, data.season !== null, state.onSeason)
        : el('div', 'tile-g heat');

  grid.append(hero, spellCard(data.groups, state));
  return grid;
}

/** Klassenaam van de gradient per soort periode. */
const GRADIENT: Record<string, string> = {
  heat: 'heat',
  cold: 'cold',
  wet: 'wet',
  dry: 'dry',
};

const SEASON_GRADIENT: Record<SeasonCard['key'], string> = {
  winter: 'cold',
  spring: 'mild',
  summer: 'heat',
  autumn: 'wet',
};

const SEASON_PLURAL: Record<SeasonCard['key'], string> = {
  winter: 'winters',
  spring: 'lentes',
  summer: 'zomers',
  autumn: 'herfsten',
};

const TITLES: Record<string, { ongoing: string; last: string; none: string }> = {
  heat: { ongoing: 'Hittegolf, dag', last: 'Laatste hittegolf', none: 'Nog geen hittegolf gemeten' },
  cold: { ongoing: 'Koudegolf, dag', last: 'Laatste koudegolf', none: 'Nog geen koudegolf gemeten' },
  wet: { ongoing: 'Natte periode, dag', last: 'Laatste natte periode', none: 'Nog geen natte periode gemeten' },
  dry: { ongoing: 'Droog, dag', last: 'Laatste droge periode', none: 'Nog geen droge periode gemeten' },
};

/** Waar de kaart naar het seizoen mag terugvallen, in dagen. */
const STALE_DAYS = 90;

function statTile(stat: Stat): string {
  const value =
    stat.unit === 'datum'
      ? stat.date
        ? formatDate(stat.date)
        : '—'
      : stat.value === null
        ? '—'
        : stat.unit === '°C'
          ? `${temp(stat.value)} °C`
          : stat.unit === 'mm'
            ? `${nl(stat.value, 0)} mm`
            : stat.unit === 'dagen'
              ? `${stat.value} dagen`
              : Number.isInteger(stat.value)
                ? String(stat.value)
                : nl(stat.value);

  // Een temperatuur zonder datum zegt half zoveel; bij tellingen zou een datum
  // juist niets betekenen.
  const when = stat.unit === '°C' && stat.date ? `<div class="w">${escapeHtml(formatDate(stat.date))}</div>` : '';

  return `<div class="hero-stat">
      <div class="k">${escapeHtml(stat.label)}</div>
      <div class="v">${escapeHtml(value)}</div>
      ${when}
    </div>`;
}

function heroCard(options: {
  gradient: string;
  badge: boolean;
  title: string;
  subtitle: string;
  stats: Stat[];
  back?: (() => void) | undefined;
}): HTMLElement {
  const card = el('div', `tile-g ${options.gradient}`);
  card.innerHTML = `
    ${options.badge ? '<div class="live-badge"><span class="dot"></span>Nu actief</div>' : ''}
    <div class="hero-title"${options.badge ? ' style="margin-top:16px"' : ''}>${escapeHtml(options.title)}</div>
    <div class="hero-sub">${escapeHtml(options.subtitle)}</div>
    <div class="hero-stats">${options.stats.map(statTile).join('')}</div>`;

  if (options.back) {
    const button = el('button', 'hero-back', 'Toon dit seizoen');
    button.type = 'button';
    button.addEventListener('click', options.back);
    card.append(button);
  }

  return card;
}

/** De laatste (of lopende) periode van één soort. */
function spellHero(group: SpellGroup, hasSeason: boolean, onSeason: () => void): HTMLElement {
  const titles = TITLES[group.kind] ?? TITLES.heat!;
  const hero = group.highlight;

  if (!hero) {
    return heroCard({
      gradient: GRADIENT[group.kind] ?? 'heat',
      badge: false,
      title: titles.none,
      subtitle: `${group.note}. De cijfers hieronder gelden voor de hele reeks.`,
      stats: group.stats,
      back: hasSeason ? onSeason : undefined,
    });
  }

  const detail =
    group.kind === 'wet'
      ? `${nl(hero.total, 0)} mm · natste dag ${nl(hero.peak, 1)} mm`
      : group.kind === 'dry'
        ? 'geen meetbare neerslag'
        : group.kind === 'cold'
          ? `dieptepunt ${temp(hero.peak)} °C · ${hero.intense} dagen < −5 °C`
          : `piek ${temp(hero.peak)} °C · ${hero.intense} dagen ≥ 30 °C`;

  const period = hero.ongoing
    ? `Sinds ${formatDateLong(hero.from)}`
    : `${formatDateLong(hero.from)} – ${formatDateLong(hero.to)}`;
  const ago = hero.ongoing ? '' : ` · ${agoLabel(hero.agoDays)}`;

  return heroCard({
    gradient: GRADIENT[group.kind] ?? 'heat',
    badge: hero.ongoing,
    title: hero.ongoing ? `${titles.ongoing} ${hero.days}` : `${titles.last} · ${hero.days} dagen`,
    subtitle: `${period} · ${detail}${ago}`,
    stats: group.stats,
    back: hasSeason && hero.agoDays > STALE_DAYS ? onSeason : undefined,
  });
}

/** Het lopende seizoen in cijfers — de terugval als er niets recents is. */
function seasonHero(season: SeasonCard): HTMLElement {
  const plural = SEASON_PLURAL[season.key];
  const comparison = season.comparison;
  const subtitle = comparison
    ? `${nl(comparison.value, Number.isInteger(comparison.value) ? 0 : 1)} ${comparison.metric} tegenover ${nl(comparison.median, Number.isInteger(comparison.median) ? 0 : 1)} als mediaan van ${comparison.seasons} ${plural}.`
    : `Nog geen eerdere ${plural} om tegen te vergelijken.`;

  return heroCard({
    gradient: SEASON_GRADIENT[season.key],
    badge: false,
    title: `${season.label} tot nu`,
    subtitle,
    stats: season.stats,
  });
}

function spellCard(groups: SpellGroup[], state: { kind: string; onKind: (k: string) => void }): HTMLElement {
  const card = el('div', 'wave-card');
  const active = groups.find((g) => g.kind === state.kind) ?? groups[0];

  card.append(
    chipRow(
      groups.map((g) => ({ key: g.kind, label: g.label })),
      active?.kind ?? '',
      state.onKind,
    ),
  );

  if (!active) return card;

  const note = el('div', 'note', escapeHtml(active.note));
  note.style.margin = '14px 0 8px';
  card.append(note);

  if (active.spells.length === 0) {
    card.append(el('p', 'empty', 'Geen periode gevonden die aan deze definitie voldoet.'));
    return card;
  }

  for (const spell of active.spells) {
    // Bij neerslag is de som informatief, bij temperatuur de piek.
    const detail =
      active.kind === 'wet'
        ? `${spell.days} dagen · ${nl(spell.total, 0)} mm`
        : active.kind === 'dry'
          ? `${spell.days} dagen`
          : `${spell.days} dagen · ${active.kind === 'cold' ? 'dieptepunt' : 'piek'} ${temp(spell.peak)} °C`;

    card.append(
      el(
        'div',
        'row',
        `<span>${escapeHtml(formatDate(spell.from))} – ${escapeHtml(spell.ongoing ? 'heden' : formatDate(spell.to))}</span>
         <span class="meta${spell.ongoing ? ' live' : ''}">${escapeHtml(detail)}</span>`,
      ),
    );
  }

  return card;
}
